import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Loader2, User, UserMinus, UserPlus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Lesson, Term } from '@court-sync/shared';
import { shouldShowEnrollmentGuardian } from '@court-sync/shared';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { EnrollStudentModal } from '@/components/schedule/EnrollStudentModal';
import { RemoveStudentEnrollmentDialog } from '@/components/lessons/RemoveStudentEnrollmentDialog';
import { EnrollmentConfirmButton } from '@/components/lessons/EnrollmentConfirmButton';
import { lessonService, type LessonEnrollmentRow } from '@/services/lesson';
import { bookingService } from '@/services/booking';
import { LessonShareTrigger } from '@/components/lessons/LessonShareTrigger';
import { getEnrollmentListScopeLabel } from '@/utils/bookingScopeLabel';

export interface TermLessonStudentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  lesson: Lesson | null;
  term: Term;
  academyId: string;
  academySlug?: string;
  makeupLessonEnabled?: boolean;
  onChanged?: () => void | Promise<void>;
}

export function TermLessonStudentsDrawer({
  isOpen,
  onClose,
  lesson,
  term,
  academyId,
  academySlug: _academySlug,
  makeupLessonEnabled = false,
  onChanged,
}: TermLessonStudentsDrawerProps) {
  const [enrollments, setEnrollments] = useState<LessonEnrollmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollModalKey, setEnrollModalKey] = useState(0);
  const [removingBookingId, setRemovingBookingId] = useState<string | null>(null);
  const [confirmingBookingId, setConfirmingBookingId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{
    bookingId: string;
    studentName: string;
    isTermEnrollment: boolean;
    sessionDateYmd?: string;
  } | null>(null);

  const loadEnrollments = useCallback(async () => {
    if (!lesson || !academyId) return;
    setLoading(true);
    try {
      const res = await lessonService.getLessonEnrollments(lesson.id, {
        academyId,
        rosterScope: 'term',
      });
      if (res.success && res.data) setEnrollments(res.data);
      else setEnrollments([]);
    } catch {
      setEnrollments([]);
      toast.error('Failed to load students for this lesson');
    } finally {
      setLoading(false);
    }
  }, [lesson, academyId]);

  useEffect(() => {
    if (isOpen && lesson) {
      setRemoveConfirm(null);
      setShowEnrollModal(false);
      void loadEnrollments();
    }
  }, [isOpen, lesson, loadEnrollments]);

  const openEnrollModal = useCallback(() => {
    setEnrollModalKey((k) => k + 1);
    setShowEnrollModal(true);
  }, []);

  const enrolledCount = useMemo(
    () => enrollments.filter((row) => row.status === 'confirmed' || row.status === 'pending').length,
    [enrollments]
  );

  const maxCapacity = lesson?.maxCapacity;
  const atCapacity =
    maxCapacity != null && maxCapacity > 0 && maxCapacity !== -1 && enrolledCount >= maxCapacity;
  const isCancelled = !!lesson?.cancelledFromDate;
  const canAddStudent = !!lesson && !isCancelled && !atCapacity;

  const handleRemove = useCallback(
    async (
      bookingId: string,
      params: { scope: 'session' | 'term'; grantMakeupCredit: boolean },
      sessionDateYmd?: string
    ): Promise<boolean> => {
      setRemovingBookingId(bookingId);
      try {
        const res = await bookingService.removeEnrollment(bookingId, {
          academyId,
          scope: params.scope,
          sessionDate: params.scope === 'session' ? sessionDateYmd : undefined,
          grantMakeupCredit: params.grantMakeupCredit,
        });
        if (res.success) {
          toast.success(
            params.scope === 'term'
              ? 'Student removed from the term lesson'
              : res.data?.makeupCreditGranted
                ? 'Student removed and make-up credit granted'
                : 'Student removed from this session'
          );
          if (
            params.scope === 'session' &&
            params.grantMakeupCredit &&
            !res.data?.makeupCreditGranted &&
            res.data?.makeupCreditSkippedReason
          ) {
            toast.error(res.data.makeupCreditSkippedReason);
          }
          await loadEnrollments();
          await onChanged?.();
          return true;
        }
        toast.error((res as { error?: string }).error || 'Failed to remove student');
        return false;
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Failed to remove student');
        return false;
      } finally {
        setRemovingBookingId(null);
      }
    },
    [academyId, loadEnrollments, onChanged]
  );

  const handleConfirmBooking = useCallback(
    async (bookingId: string) => {
      setConfirmingBookingId(bookingId);
      try {
        const res = await bookingService.updateBooking(bookingId, { status: 'confirmed' });
        if (res.success) {
          toast.success('Booking confirmed');
          await loadEnrollments();
          await onChanged?.();
        } else {
          toast.error((res as { error?: string }).error || 'Failed to confirm booking');
        }
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Failed to confirm booking');
      } finally {
        setConfirmingBookingId(null);
      }
    },
    [loadEnrollments, onChanged]
  );

  const handleClose = useCallback(() => {
    setShowEnrollModal(false);
    onClose();
  }, [onClose]);

  if (!lesson) return null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        title="Students"
        description={lesson.name}
        width="3xl"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <LessonShareTrigger
              academyId={academyId}
              lessonId={lesson.id}
              lessonName={lesson.name}
              lessonType={lesson.lessonType}
              lessonTermId={lesson.termId}
              lessonIsActive={lesson.isActive}
              term={term}
              enrollments={enrollments}
              onAfterNotify={loadEnrollments}
              stackLevel="default"
            />
          </div>
        }
      >
        <div className="space-y-6 text-sm text-gray-800 dark:text-gray-200">
          <div className="rounded-lg border border-sky-200 bg-sky-50/90 px-3 py-2.5 text-xs text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100">
            <p>
              Students added here are enrolled for the <strong>entire term</strong> ({term.name}).
              To add or remove a student for <strong>one session only</strong>, open{' '}
              <Link
                to="/admin/calendar"
                className="font-medium text-sky-900 underline underline-offset-2 hover:text-sky-950 dark:text-sky-100 dark:hover:text-white"
              >
                Calendar
              </Link>{' '}
              and select the lesson on a specific date.
            </p>
          </div>

          {isCancelled && (
            <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              This lesson was cancelled for the term. You can review enrollments but cannot add new
              students.
            </p>
          )}

          {atCapacity && !isCancelled && (
            <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              This lesson is at capacity ({enrolledCount}/{maxCapacity}). Remove a student to add
              another, or increase max capacity when editing the lesson.
            </p>
          )}

          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                <Users className="h-4 w-4" aria-hidden />
                Enrolled students
                {maxCapacity != null && maxCapacity !== -1 ? (
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    ({enrolledCount} / {maxCapacity})
                  </span>
                ) : (
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    ({enrolledCount})
                  </span>
                )}
              </h4>
              {canAddStudent && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={openEnrollModal}
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Add student
                </Button>
              )}
            </div>
            {loading ? (
              <p className="text-xs text-gray-500">Loading…</p>
            ) : enrollments.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">No students enrolled yet.</p>
            ) : (
              <ul className="max-h-[min(16rem,40vh)] space-y-2 overflow-y-auto pr-1">
                {enrollments.map((row) => {
                  const isMakeup =
                    row.enrollmentSource === 'makeup_session' || row.isMakeupBooking;
                  const scopeLabel =
                    !isMakeup && row.bookingScope === 'session'
                      ? getEnrollmentListScopeLabel({
                          bookingScope: row.bookingScope,
                          lessonType: lesson.lessonType,
                          lessonTermId: lesson.termId,
                        })
                      : null;
                  const fullScopeLabel =
                    getEnrollmentListScopeLabel({
                      bookingScope: row.bookingScope,
                      lessonType: lesson.lessonType,
                      lessonTermId: lesson.termId,
                    }) ?? null;
                  const isTermEnrollment =
                    row.bookingScope === 'term' || row.bookingScope === 'package';

                  return (
                    <li
                      key={row.bookingId ?? row.makeupSessionReservationId ?? row.studentId}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 last:border-0 dark:border-gray-700"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {row.studentName}
                          </span>
                          {row.isMakeupBooking && (
                            <span className="rounded bg-purple-100 px-1.5 py-0 text-[10px] text-purple-900 dark:bg-purple-900/50 dark:text-purple-100">
                              Make-up
                            </span>
                          )}
                          {scopeLabel && (
                            <span className="rounded bg-sky-100 px-1.5 py-0 text-[10px] text-sky-900 dark:bg-sky-900/50 dark:text-sky-100">
                              {scopeLabel}
                            </span>
                          )}
                          {row.status === 'pending' && (
                            <span className="rounded bg-amber-100 px-1.5 py-0 text-[10px] text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
                              Unconfirmed
                            </span>
                          )}
                          {fullScopeLabel && !scopeLabel ? (
                            <span className="text-xs text-gray-500">{fullScopeLabel}</span>
                          ) : null}
                        </div>
                        {shouldShowEnrollmentGuardian({
                          isSelf: row.isSelf,
                          isAdultStudent: row.isAdultStudent,
                          isManual: row.isManual,
                          hasLinkedGuardian: !!row.parentId,
                        }) &&
                          (row.parentName || row.parentEmail) && (
                            <p className="mt-0.5 text-xs text-gray-500">
                              Guardian: {row.parentName || '—'}
                              {row.parentEmail ? ` · ${row.parentEmail}` : ''}
                            </p>
                          )}
                      </div>
                      {row.bookingId && (
                        <div className="flex shrink-0 flex-wrap items-center gap-1">
                          {row.status === 'pending' ? (
                            <EnrollmentConfirmButton
                              bookingId={row.bookingId}
                              confirmingBookingId={confirmingBookingId}
                              disabled={removingBookingId === row.bookingId}
                              onConfirm={handleConfirmBooking}
                            />
                          ) : null}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 shrink-0 gap-1.5 text-red-700 dark:text-red-300"
                            disabled={
                              removingBookingId === row.bookingId ||
                              confirmingBookingId === row.bookingId
                            }
                            onClick={() =>
                              setRemoveConfirm({
                                bookingId: row.bookingId!,
                                studentName: row.studentName,
                                isTermEnrollment,
                                sessionDateYmd: row.sessionDateYmd,
                              })
                            }
                          >
                            {removingBookingId === row.bookingId ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                …
                              </>
                            ) : (
                              <>
                                <UserMinus className="h-4 w-4" aria-hidden />
                                Remove
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {canAddStudent && !loading && enrollments.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-muted/20 px-4 py-6 text-center dark:border-gray-600">
              <User className="mx-auto mb-2 h-8 w-8 text-gray-400" aria-hidden />
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Enroll from your student roster or add a manual student without a student or guardian
                account.
              </p>
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={openEnrollModal}>
                <UserPlus className="h-4 w-4" aria-hidden />
                Add student
              </Button>
            </div>
          )}

          {!canAddStudent && !isCancelled && enrollments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-muted/20 px-4 py-6 text-center dark:border-gray-600">
              <User className="mx-auto mb-2 h-8 w-8 text-gray-400" aria-hidden />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No students enrolled yet. This lesson is full — increase capacity in Edit to add
                more.
              </p>
              <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" asChild>
                <Link to="/admin/calendar">
                  <CalendarDays className="h-4 w-4" aria-hidden />
                  Open Calendar
                </Link>
              </Button>
            </div>
          ) : null}
        </div>
      </Drawer>

      <EnrollStudentModal
        key={`term-enroll-${lesson.id}-${enrollModalKey}`}
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        lessonId={lesson.id}
        lessonName={lesson.name}
        academyId={academyId}
        termId={term.id}
        maxCapacity={maxCapacity !== -1 ? maxCapacity : undefined}
        enrolledCount={enrolledCount}
        stayOpenAfterEnroll
        onEnrollSuccess={async () => {
          await loadEnrollments();
          await onChanged?.();
        }}
      />

      <RemoveStudentEnrollmentDialog
        key={removeConfirm ? `remove-${removeConfirm.bookingId}` : 'remove-closed'}
        isOpen={!!removeConfirm}
        studentName={removeConfirm?.studentName ?? ''}
        lessonName={lesson.name}
        sessionDateLabel=""
        sessionDateYmd={removeConfirm?.sessionDateYmd ?? ''}
        bookingId={removeConfirm?.bookingId ?? ''}
        makeupLessonEnabled={makeupLessonEnabled}
        isTermEnrollment={removeConfirm?.isTermEnrollment ?? true}
        isSubmitting={!!removeConfirm && removingBookingId === removeConfirm.bookingId}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={async (params) => {
          if (!removeConfirm) return;
          const ok = await handleRemove(
            removeConfirm.bookingId,
            params,
            removeConfirm.sessionDateYmd
          );
          if (ok) setRemoveConfirm(null);
        }}
      />
    </>
  );
}
